import { cn } from "@/lib/utils";
import {
  Building2,
  BookOpen,
  Bot,
  FileSearch,
  LifeBuoy,
  LogOut,
  LoaderCircle,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Brand } from "../components/Brand";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { PAGE_WIDTH } from "./page-width";

interface OperationsNavigationItem {
  active: boolean;
  href: string;
  icon: LucideIcon;
  label: string;
}

function navigationItems({
  canAudit,
  canDocs,
  canSupport,
  canManageEmployees,
  customerSelected,
  path,
}: {
  canAudit: boolean;
  canDocs: boolean;
  canSupport: boolean;
  canManageEmployees: boolean;
  customerSelected: boolean;
  path: string;
}): OperationsNavigationItem[] {
  return [
    {
      active: path === "/" || customerSelected,
      href: "/",
      icon: Building2,
      label: "Customers",
    },
    ...(canSupport
      ? [
          {
            active: path === "/support" || path.startsWith("/support/"),
            href: "/support",
            icon: LifeBuoy,
            label: "Support",
          },
        ]
      : []),
    {
      active: path === "/provisioning",
      href: "/provisioning",
      icon: FileSearch,
      label: "Provisioning",
    },
    ...(canDocs
      ? [
          {
            active: path === "/docs" || path.startsWith("/docs/"),
            href: "/docs",
            icon: BookOpen,
            label: "Docs",
          },
        ]
      : []),
    {
      active: path === "/ai-access",
      href: "/ai-access",
      icon: Bot,
      label: "AI access",
    },
    ...(canAudit
      ? [
          {
            active: path === "/audit",
            href: "/audit",
            icon: ShieldCheck,
            label: "Audit",
          },
        ]
      : []),
    ...(canManageEmployees
      ? [
          {
            active: path === "/employees",
            href: "/employees",
            icon: UsersRound,
            label: "Employees",
          },
        ]
      : []),
  ];
}

function OperationsNavigation({
  canAudit,
  canDocs,
  canSupport,
  canManageEmployees,
  collapsed,
  customerSelected,
  navigate,
  path,
}: {
  canAudit: boolean;
  canDocs: boolean;
  canSupport: boolean;
  canManageEmployees: boolean;
  collapsed: boolean;
  customerSelected: boolean;
  navigate: OperationsNavigate;
  path: string;
}) {
  return (
    <nav aria-label="Operations" className="space-y-1" id="operations-sidebar-navigation">
      {navigationItems({
        canAudit,
        canDocs,
        canManageEmployees,
        canSupport,
        customerSelected,
        path,
      }).map((item) => (
        <OperationsNavigationLink
          collapsed={collapsed}
          item={item}
          key={item.href}
          navigate={navigate}
        />
      ))}
    </nav>
  );
}

function OperationsNavigationLink({
  collapsed,
  item,
  navigate,
}: {
  collapsed: boolean;
  item: OperationsNavigationItem;
  navigate: OperationsNavigate;
}) {
  const Icon = item.icon;
  const iconOnly = collapsed;
  return (
    <OperationsLink
      ariaCurrent={item.active ? "page" : undefined}
      className={cn(
        "relative flex min-h-11 items-center rounded-sm text-sm font-semibold no-underline transition-[color,background-color,border-color] duration-150 ease-out focus-visible:z-10",
        "grid grid-cols-[40px_minmax(0,1fr)] px-0 py-0.5",
        item.active
          ? "bg-info-background text-link"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        item.active &&
          "before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-primary",
      )}
      href={item.href}
      navigate={navigate}
      title={iconOnly ? item.label : undefined}
    >
      <span className="flex size-10 items-center justify-center">
        <Icon aria-hidden="true" className="size-4 shrink-0" />
      </span>
      <span className={iconOnly ? "sr-only" : undefined}>{item.label}</span>
    </OperationsLink>
  );
}

function SidebarAccount({
  displayName,
  onLogout,
  isSigningOut,
  collapsed = false,
}: {
  displayName: string;
  onLogout: () => void;
  isSigningOut: boolean;
  collapsed?: boolean;
}) {
  return (
    <div className={cn("flex items-center gap-2 px-1", collapsed && "flex-col")}>
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
        title={displayName}
        aria-label={collapsed ? displayName : undefined}
      >
        {displayName.slice(0, 1).toUpperCase()}
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate text-sm">{displayName}</span>}
      <Button
        onClick={onLogout}
        size="icon-sm"
        variant="ghost"
        aria-label={isSigningOut ? "Signing out…" : "Sign out"}
        title={isSigningOut ? "Signing out…" : "Sign out"}
        disabled={isSigningOut}
      >
        {isSigningOut ? (
          <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
        ) : (
          <LogOut />
        )}
      </Button>
    </div>
  );
}

function MobileNavigation(props: OperationsShellProps & { isSigningOut: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex h-12 shrink-0 items-center gap-3 border-b bg-sidebar px-3 md:hidden">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger
          render={<Button size="icon-sm" variant="ghost" aria-label="Open navigation" />}
        >
          <Menu />
        </DialogTrigger>
        <DialogContent className="fixed inset-y-0 left-0 m-0 h-dvh max-h-none w-72 translate-x-0 translate-y-0 rounded-none">
          <DialogHeader className="px-4 pt-5">
            <DialogTitle className="sr-only">Navigation</DialogTitle>
            <Brand className="dark:[&_img]:brightness-0 dark:[&_img]:invert" />
          </DialogHeader>
          <DialogBody className="px-2">
            <OperationsNavigation
              {...props}
              canSupport={props.canSupport ?? false}
              collapsed={false}
              navigate={(path) => {
                props.navigate(path);
                setOpen(false);
              }}
            />
          </DialogBody>
          <DialogFooter className="block px-2 py-3">
            <SidebarAccount
              displayName={props.displayName}
              onLogout={props.onLogout}
              isSigningOut={props.isSigningOut}
            />
            {props.signOutError ? <FieldError>{props.signOutError}</FieldError> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Brand compact className="[&_svg]:text-link" />
    </div>
  );
}

function OperationsSidebar({
  canAudit,
  canDocs,
  canSupport,
  canManageEmployees,
  collapsed,
  customerSelected,
  navigate,
  onCollapsedChange,
  displayName,
  onLogout,
  isSigningOut,
  path,
}: {
  canAudit: boolean;
  canDocs: boolean;
  canSupport: boolean;
  canManageEmployees: boolean;
  collapsed: boolean;
  customerSelected: boolean;
  navigate: OperationsNavigate;
  onCollapsedChange: (collapsed: boolean) => void;
  displayName: string;
  onLogout: () => void;
  isSigningOut: boolean;
  path: string;
}) {
  return (
    <aside
      className={cn(
        "hidden min-h-0 shrink-0 flex-col border-r bg-sidebar transition-[width] duration-150 ease-out md:flex",
        collapsed ? "w-16" : "w-56",
      )}
      data-collapsed={collapsed}
      data-testid="operations-sidebar"
    >
      <div className={cn("flex h-20 shrink-0 items-center", collapsed ? "justify-center" : "px-5")}>
        <Brand
          compact={collapsed}
          className="dark:[&_img]:brightness-0 dark:[&_img]:invert [&_svg]:text-link"
        />
      </div>
      <div className="relative min-h-0 flex-1 overflow-y-auto px-2 py-3">
        <OperationsNavigation
          canAudit={canAudit}
          canDocs={canDocs}
          canSupport={canSupport}
          canManageEmployees={canManageEmployees}
          collapsed={collapsed}
          customerSelected={customerSelected}
          navigate={navigate}
          path={path}
        />
      </div>
      <div className="shrink-0 space-y-2 border-t px-2 py-3">
        <SidebarAccount
          displayName={displayName}
          onLogout={onLogout}
          isSigningOut={isSigningOut}
          collapsed={collapsed}
        />
        <Button
          aria-controls="operations-sidebar-navigation"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn("w-full", collapsed ? "px-0" : "justify-start")}
          onClick={() => {
            onCollapsedChange(!collapsed);
          }}
          title={collapsed ? "Expand sidebar" : undefined}
          type="button"
          variant="ghost"
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          {collapsed ? null : <span>Collapse sidebar</span>}
        </Button>
      </div>
    </aside>
  );
}

interface OperationsShellProps {
  canAudit: boolean;
  canDocs: boolean;
  /** ADR-0089: shows Support to employees who may read support tickets. */
  canSupport?: boolean;
  canManageEmployees: boolean;
  children: ReactNode;
  customerSelected: boolean;
  displayName: string;
  navigate: OperationsNavigate;
  onLogout: () => void;
  isSigningOut?: boolean;
  signOutError?: string;
  path: string;
}

export function OperationsShell({
  canAudit,
  canDocs,
  canSupport = false,
  canManageEmployees,
  children,
  customerSelected,
  displayName,
  navigate,
  onLogout,
  isSigningOut = false,
  signOutError,
  path,
}: OperationsShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Docs and the support desk (ADR-0089) lay out their own columns across the full width.
  const docsLayout = ["/docs", "/support"].some(
    (root) => path === root || path.startsWith(`${root}/`),
  );
  return (
    // `relative` on the frame and its scroll area: a positioned descendant, such as a visually hidden
    // file input, anchors inside them instead of stretching the window past the frame.
    <div className="relative flex h-dvh min-h-0 flex-col overflow-hidden bg-background">
      <a className="skip-link" href="#operations-main">
        Skip to operations content
      </a>
      <MobileNavigation
        canAudit={canAudit}
        canDocs={canDocs}
        canSupport={canSupport}
        canManageEmployees={canManageEmployees}
        customerSelected={customerSelected}
        displayName={displayName}
        navigate={navigate}
        onLogout={onLogout}
        isSigningOut={isSigningOut}
        {...(signOutError ? { signOutError } : {})}
        path={path}
      >
        {null}
      </MobileNavigation>
      <div className="flex min-h-0 flex-1">
        <OperationsSidebar
          canAudit={canAudit}
          canDocs={canDocs}
          canSupport={canSupport}
          canManageEmployees={canManageEmployees}
          collapsed={sidebarCollapsed}
          customerSelected={customerSelected}
          navigate={navigate}
          onCollapsedChange={setSidebarCollapsed}
          displayName={displayName}
          onLogout={onLogout}
          isSigningOut={isSigningOut}
          path={path}
        />
        <main
          className="relative min-w-0 flex-1 overflow-y-auto overscroll-contain bg-canvas"
          data-testid="operations-main"
          id="operations-main"
          tabIndex={-1}
        >
          <div
            className={cn(
              "mx-auto w-full",
              docsLayout
                ? "max-w-none"
                : customerSelected
                  ? [PAGE_WIDTH, "px-5 py-4 sm:px-6 sm:py-7 lg:px-8 lg:py-9"]
                  : [PAGE_WIDTH, "px-5 py-7 sm:px-6 lg:px-8 lg:py-9"],
            )}
            data-layout={docsLayout ? "wide" : "standard"}
            data-testid="operations-content"
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
